import { UserRole, type StaffActor, type StaffLoginBody } from '@hadir/shared';
import { create } from 'zustand';

import {
  fetchMe,
  setSessionBridge,
  staffActorOf,
  staffLogin,
  staffLogout,
  staffRefresh,
} from '@/lib/api';

/**
 * Staff session state (05 §2.1, §5.1; 04 §3).
 *
 * The access token lives **in memory only** — never localStorage, never a
 * non-httpOnly cookie. Durability comes from the httpOnly refresh cookie
 * `hadir_rt` (SameSite=Strict, Path=/api/v1/auth), which the browser sends to
 * `POST /auth/staff/refresh` on boot and whenever a 401 is retried.
 *
 * Dependency direction: this module imports the API client, never the other way
 * round. The client reaches back through the session bridge installed at the
 * bottom of this file, so the module graph stays acyclic.
 */

export type Role = UserRole;

/** The staff actor (04 §3.4). Re-exported so callers need one import. */
export type { StaffActor };

/**
 * `unknown` is the boot state, before the silent refresh has resolved. Guards
 * must wait it out rather than redirecting, otherwise every reload bounces a
 * signed-in TA to /login.
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface AuthState {
  accessToken: string | null;
  /** Client-clock ms at which the access token expires (from `expiresIn`). */
  expiresAt: number | null;
  actor: StaffActor | null;
  status: AuthStatus;

  /** Store a fresh session after login or refresh. `expiresIn` is in seconds. */
  setSession: (session: { accessToken: string; expiresIn: number; actor?: StaffActor }) => void;
  /** Mark the boot refresh as finished with no session. */
  setAnonymous: () => void;
  /** Drop everything (logout, refresh reuse, unrecoverable 401). */
  clear: () => void;

  /** `POST /auth/staff/login`; resolves with the signed-in actor, throws `ApiError`. */
  login: (credentials: StaffLoginBody) => Promise<StaffActor>;
  /** `POST /auth/staff/logout`; always ends anonymous, even if the call fails. */
  logout: () => Promise<void>;
  /**
   * Silent session restore on app start: exchange the refresh cookie for an
   * access token, then resolve the actor from `GET /auth/me`. Idempotent and
   * single-flight, so React 19 StrictMode's double effect makes one round trip.
   */
  bootstrap: () => Promise<void>;

  hasRole: (...roles: Role[]) => boolean;
  isAdmin: () => boolean;
}

const emptySession = {
  accessToken: null,
  expiresAt: null,
  actor: null,
} as const;

/**
 * Proactive refresh (05 §4.1): renew at ~87 % of the token's life so a long
 * lecture never has to discover the expiry through a 401.
 */
let renewTimer: ReturnType<typeof setTimeout> | null = null;

function clearRenewTimer(): void {
  if (renewTimer !== null) {
    clearTimeout(renewTimer);
    renewTimer = null;
  }
}

function armRenewTimer(expiresInSec: number): void {
  clearRenewTimer();
  // Below a minute of life the timer is pointless — the 401 path covers it.
  if (!Number.isFinite(expiresInSec) || expiresInSec < 60) return;
  const delayMs = Math.floor(expiresInSec * 0.87) * 1000;
  renewTimer = setTimeout(() => {
    renewTimer = null;
    if (useAuthStore.getState().status !== 'authenticated') return;
    void staffRefresh()
      .then((result) => {
        useAuthStore.getState().setSession(result);
      })
      .catch(() => {
        // A failed proactive refresh is not proof the session is dead (it may be
        // the network); the next request's 401 path makes the real decision.
      });
  }, delayMs);
}

let bootstrapInFlight: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()((set, get) => ({
  ...emptySession,
  status: 'unknown',

  setSession: ({ accessToken, expiresIn, actor }) => {
    armRenewTimer(expiresIn);
    set((state) => ({
      accessToken,
      expiresAt: Date.now() + expiresIn * 1000,
      // A refresh response carries no actor; keep the one we already have.
      actor: actor ?? state.actor,
      status: 'authenticated',
    }));
  },

  setAnonymous: () => {
    clearRenewTimer();
    set({ ...emptySession, status: 'anonymous' });
  },

  clear: () => {
    clearRenewTimer();
    set({ ...emptySession, status: 'anonymous' });
  },

  login: async (credentials) => {
    const result = await staffLogin(credentials);
    get().setSession({
      accessToken: result.accessToken,
      expiresIn: result.expiresIn,
      actor: result.actor,
    });
    return result.actor;
  },

  logout: async () => {
    try {
      await staffLogout();
    } catch {
      // The cookie may already be gone; the local session still has to end.
    } finally {
      get().clear();
    }
  },

  bootstrap: async () => {
    if (bootstrapInFlight) return bootstrapInFlight;

    bootstrapInFlight = (async () => {
      try {
        const session = await staffRefresh();
        get().setSession(session);
        const me = await fetchMe();
        const actor = staffActorOf(me);
        if (!actor) {
          // A student token in a dashboard tab is not a staff session.
          get().setAnonymous();
          return;
        }
        set({ actor, status: 'authenticated' });
      } catch {
        // No cookie, an expired one, or the API is down: either way there is no
        // session to restore, and the guards may now decide.
        get().setAnonymous();
      } finally {
        bootstrapInFlight = null;
      }
    })();

    return bootstrapInFlight;
  },

  hasRole: (...roles) => {
    const role = get().actor?.role;
    return role !== undefined && roles.includes(role);
  },

  isAdmin: () => get().actor?.role === UserRole.ADMIN,
}));

/** Non-hook accessor for modules outside React (api client, socket factory). */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function isAuthenticated(): boolean {
  return useAuthStore.getState().status === 'authenticated';
}

/** Reactive `isAdmin` for components: `const isAdmin = useIsAdmin();`. */
export function useIsAdmin(): boolean {
  return useAuthStore((state) => state.actor?.role === UserRole.ADMIN);
}

/** Test seam: forget an in-flight bootstrap and any pending renew timer. */
export function resetAuthStore(): void {
  bootstrapInFlight = null;
  clearRenewTimer();
  useAuthStore.setState({ ...emptySession, status: 'unknown' });
}

/*
 * Wire the client to this store. Import order does not matter: the client holds
 * a no-op bridge until this module is evaluated, and every module that issues a
 * request also (transitively) imports the store.
 */
setSessionBridge({
  getAccessToken: () => useAuthStore.getState().accessToken,
  onRefreshed: (session) => useAuthStore.getState().setSession(session),
  onUnauthenticated: () => useAuthStore.getState().clear(),
});
